import { useEffect, useRef } from 'react'
import { Box, MousePointer2 } from 'lucide-react'
import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader.js'

const imageExtensions = new Set(['.png', '.jpg', '.jpeg', '.webp'])

function ThreePreview({ file }) {
  const mountRef = useRef(null)

  useEffect(() => {
    const mount = mountRef.current
    if (!mount || !file) return undefined

    const scene = new THREE.Scene()
    const camera = new THREE.PerspectiveCamera(32, 1, 0.01, 10000)
    camera.up.set(0, 0, 1)
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true })
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    renderer.outputColorSpace = THREE.SRGBColorSpace
    renderer.toneMapping = THREE.ACESFilmicToneMapping
    renderer.toneMappingExposure = 1.05
    mount.appendChild(renderer.domElement)

    const controls = new OrbitControls(camera, renderer.domElement)
    controls.enableDamping = true
    controls.dampingFactor = 0.07
    controls.autoRotate = true
    controls.autoRotateSpeed = 0.7

    scene.add(new THREE.HemisphereLight(0xf8f3e8, 0x111315, 2.4))
    const key = new THREE.DirectionalLight(0xffe4ca, 4.4)
    key.position.set(4, -5, 7)
    scene.add(key)
    const rim = new THREE.DirectionalLight(0xff6a00, 2.2)
    rim.position.set(-5, 3, 2)
    scene.add(rim)

    const grid = new THREE.GridHelper(240, 48, 0x4b4f52, 0x25292c)
    grid.rotation.x = Math.PI / 2
    grid.position.z = -0.01
    grid.material.opacity = 0.5
    grid.material.transparent = true
    scene.add(grid)

    const modelRoot = new THREE.Group()
    scene.add(modelRoot)

    function fitModel(object) {
      const box = new THREE.Box3().setFromObject(object)
      if (box.isEmpty()) return
      const size = box.getSize(new THREE.Vector3())
      const center = box.getCenter(new THREE.Vector3())
      object.position.sub(center)
      object.position.z += size.z / 2
      const max = Math.max(size.x, size.y, size.z, 1)
      grid.scale.setScalar(Math.max(1, max / 100))
      camera.near = max / 1000
      camera.far = max * 100
      camera.position.set(max * 2.15, -max * 2.55, max * 1.85)
      camera.updateProjectionMatrix()
      controls.target.set(0, 0, size.z * 0.32)
      controls.update()
    }

    const extension = file.extension.toLowerCase()
    if (extension === '.stl') {
      new STLLoader().load(file.url, (geometry) => {
        geometry.computeVertexNormals()
        const mesh = new THREE.Mesh(
          geometry,
          new THREE.MeshStandardMaterial({ color: 0x777a7d, roughness: 0.62, metalness: 0.05 }),
        )
        modelRoot.add(mesh)
        fitModel(mesh)
      })
    } else {
      new GLTFLoader().load(file.url, (gltf) => {
        gltf.scene.traverse((node) => {
          if (node.isMesh) {
            node.castShadow = true
            node.receiveShadow = true
            if (node.material) node.material.side = THREE.DoubleSide
          }
        })
        modelRoot.add(gltf.scene)
        fitModel(gltf.scene)
      })
    }

    const resize = () => {
      const width = mount.clientWidth
      const height = mount.clientHeight
      renderer.setSize(width, height, false)
      camera.aspect = width / Math.max(height, 1)
      camera.updateProjectionMatrix()
    }
    const observer = new ResizeObserver(resize)
    observer.observe(mount)
    resize()

    let frame
    const render = () => {
      controls.update()
      renderer.render(scene, camera)
      frame = requestAnimationFrame(render)
    }
    render()

    return () => {
      cancelAnimationFrame(frame)
      observer.disconnect()
      controls.dispose()
      renderer.dispose()
      scene.traverse((node) => {
        node.geometry?.dispose?.()
        if (Array.isArray(node.material)) node.material.forEach((material) => material.dispose())
        else node.material?.dispose?.()
      })
      renderer.domElement.remove()
    }
  }, [file])

  return (
    <div className="three-preview" ref={mountRef}>
      <span className="preview-help"><MousePointer2 size={13} /> arraste para girar</span>
    </div>
  )
}

export default function ModelPreview({ project }) {
  const files = project?.files || []
  const model = files.find((file) => file.relativePath.startsWith('previews/') && ['.glb', '.gltf'].includes(file.extension))
    || files.find((file) => file.relativePath.startsWith('stl/') && file.extension === '.stl')
    || files.find((file) => ['.glb', '.gltf', '.stl'].includes(file.extension))
  const image = files.find((file) => file.relativePath.startsWith('previews/') && imageExtensions.has(file.extension))
    || files.find((file) => imageExtensions.has(file.extension))

  if (model) return <ThreePreview key={model.url} file={model} />
  if (image) return <img className="image-preview" src={image.url} alt={`Prévia de ${project.title}`} />

  return (
    <div className="empty-preview">
      <div className="empty-cube"><Box size={50} strokeWidth={1.25} /></div>
      <strong>O modelo aparecerá aqui</strong>
      <p>Adicione um STL, GLB ou imagem de prévia ao projeto.</p>
    </div>
  )
}
